import type React from "react";
import { memo } from "react";
import ReactHlsPlayer from "react-hls-player";
import { useTranslation } from "react-i18next";
import { useKioskTheme } from "../context/KioskThemeContext";

interface MainVideoPlayerProps {
	currentVideoUrl: string | null;
	displayStage: string;
	gameState?: string;
	handleMainVideoEnded: () => void;
	handleVideoError?: () => void;
	onTimeUpdate?: () => void;
	playerRef: React.RefObject<HTMLVideoElement | null>;
	className?: string;
	label?: string;
	replayTeamIcon?: string;
	replayTeamName?: string;
}

export const MainVideoPlayer: React.FC<MainVideoPlayerProps> = memo(
	({
		currentVideoUrl,
		displayStage,
		gameState = "",
		handleMainVideoEnded,
		handleVideoError,
		onTimeUpdate,
		playerRef,
		className = "w-full h-full object-contain",
		label,
		replayTeamIcon,
		replayTeamName,
	}) => {
		const { t } = useTranslation();
		const { theme } = useKioskTheme();
		const renderVideoContent = () => {
			if (!currentVideoUrl) return <div className="w-full h-full bg-black" />;

			if (currentVideoUrl === "LIVE_FEED_PLACEHOLDER") {
				return (
					<div className="w-full h-full bg-kiosk-surface-secondary flex items-center justify-center">
						<div className="text-white flex flex-col items-center animate-pulse">
							<div className="text-4xl">🔴</div>
							<span className="font-bebas text-2xl mt-2">LIVE FEED</span>
						</div>
					</div>
				);
			}

			const isMuted = displayStage === "idle" || displayStage === "waiting";
			const isHls =
				currentVideoUrl.includes(".m3u8") || currentVideoUrl.includes("/hls/");

			return (
				<div className="w-full h-full bg-black flex items-center justify-center overflow-hidden">
					{isHls ? (
						<ReactHlsPlayer
							src={currentVideoUrl}
							autoPlay={true}
							controls={false}
							width="100%"
							height="100%"
							muted={isMuted}
							onEnded={handleMainVideoEnded}
							onError={handleVideoError}
							onTimeUpdate={onTimeUpdate}
							playerRef={playerRef as any}
							className={className}
						/>
					) : (
						<video
							ref={playerRef as any}
							src={currentVideoUrl}
							autoPlay
							muted={isMuted}
							onEnded={handleMainVideoEnded}
							onError={handleVideoError}
							onStalled={handleVideoError}
							onTimeUpdate={onTimeUpdate}
							className={className}
							width="100%"
							height="100%"
						/>
					)}
				</div>
			);
		};
		const renderLabel = () => {
			if (label === "replay")
				return (
					<div
						className={`absolute flex z-20 ${gameState === "new_shot" ? "left-10 top-[128px]" : displayStage === "endgame" ? "left-5 top-[106px]" : "left-0 top-0"}`}
					>
						<div
							className={`py-1.5 px-3 flex items-center gap-2 bg-kiosk-accent ${theme === "brand" ? "text-white" : "text-kiosk-surface-primary"} ${displayStage === "endgame" ? "py-2.5 px-4" : "py-2.5 px-4"}`}
						>
							<span
								className={`inline-flex w-0 h-0 border-t-4 border-t-transparent border-b-4 border-b-transparent border-l-[9px] ${theme === "brand" ? "border-white" : "border-kiosk-surface-primary"}`}
							></span>
							<p
								className={
									displayStage === "endgame"
										? "text-display-h4-b"
										: "text-body-xl-b"
								}
							>
								{t("kiosk.playing.replay")}
							</p>
						</div>
						<div className="py-2.5 px-4 flex items-center gap-2 bg-kiosk-surface-primary text-white">
							<div className="w-10 h-10 flex items-center justify-center rounded-3xl border border-white/20 bg-kiosk-surface-secondary">
								<img
									src={replayTeamIcon || "/img/event-page/team_light.svg"}
									alt={replayTeamName || "Team"}
									className="w-10 h-10"
								/>
							</div>
							<p
								className={
									displayStage === "endgame"
										? "text-display-h4-b"
										: "text-body-xl-b"
								}
							>
								{replayTeamName || "TEAM A"}
							</p>
						</div>
					</div>
				);
			if (label === "live")
				return (
					<div
						className={`absolute flex items-center gap-2 bg-kiosk-surface-primary ${theme === "brand" ? "text-white" : "text-kiosk-accent"} ${["regular", "ending"].includes(gameState) ? "left-5 top-[128px] py-2.5 px-4" : "left-0 top-4 py-1.5 px-3"}`}
					>
						<span
							className={`block rounded-md ${theme === "brand" ? "bg-white" : "bg-kiosk-accent"} ${["regular", "ending"].includes(gameState) ? "w-2.5 h-2.5" : "w-2 h-2"}`}
						></span>
						<p
							className={
								["regular", "ending"].includes(gameState)
									? "text-display-h4-b"
									: "text-body-xl-b"
							}
						>
							{t("kiosk.playing.live")}
						</p>
					</div>
				);
		};
		return (
			<div className="relative w-full h-full">
				{renderVideoContent()}
				{label && renderLabel()}
			</div>
		);
	},
);
